import { useCallback, useEffect, useRef, useState } from "react"

import JSON5 from "json5"

import { logger } from "@/lib/logger"

const log = logger("SessionStorage")
export function useSessionStorage<T>(
  key: string | undefined,
  initialValue: T,
  options?: { deferRead?: boolean; strictPersistence?: boolean },
): [T, (value: T | ((prev: T) => T)) => boolean, boolean] {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (options?.deferRead) {
      return initialValue
    }
    if (typeof window === "undefined" || !key) {
      return initialValue
    }

    try {
      const item = window.sessionStorage.getItem(key)
      return item ? JSON5.parse(item) : initialValue
    } catch (error) {
      log.warn(`Error reading sessionStorage key "${key}"`, { error, key })
      return initialValue
    }
  })
  const [hydratedKey, setHydratedKey] = useState<string | undefined>(() =>
    options?.deferRead ? undefined : key,
  )
  useEffect(() => {
    if (!options?.deferRead) {
      return
    }
    if (typeof window === "undefined" || !key) {
      setStoredValue(initialValue)
      setHydratedKey(undefined)
      return
    }
    try {
      const item = window.sessionStorage.getItem(key)
      setStoredValue(item ? JSON5.parse(item) : initialValue)
    } catch (error) {
      log.warn(`Error reading sessionStorage key "${key}"`, { error, key })
      setStoredValue(initialValue)
    }
    setHydratedKey(key)
  }, [initialValue, key, options?.deferRead])

  // Use a ref to always access the latest storedValue without including it in dependencies
  const storedValueRef = useRef(storedValue)
  useEffect(() => {
    storedValueRef.current = storedValue
  }, [storedValue])

  // Return a wrapped version of useState's setter function that persists the new value to sessionStorage
  // Memoized with useCallback to ensure stable reference
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      const valueToStore =
        value instanceof Function ? value(storedValueRef.current) : value
      try {
        if (
          options?.strictPersistence &&
          (typeof window === "undefined" || !key)
        ) {
          return false
        }
        if (typeof window !== "undefined" && key) {
          window.sessionStorage.setItem(key, JSON.stringify(valueToStore))
        }
        setStoredValue(valueToStore)
        return true
      } catch (error) {
        log.warn(`Error setting sessionStorage key "${key}"`, { error, key })
        if (options?.strictPersistence) {
          return false
        }
        setStoredValue(valueToStore)
        return true
      }
    },
    [key, options?.strictPersistence],
  )

  const isHydrated =
    !options?.deferRead || (key !== undefined && hydratedKey === key)

  return [storedValue, setValue, isHydrated]
}
